import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';
import { useSessionTimeout } from '../hooks/useSessionTimeout';

const AuthContext = createContext(null);

/**
 * AuthProvider component that wraps the app and provides authentication state
 */
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('userInfo');
    return stored ? JSON.parse(stored) : null;
  });

  const isLoggedIn = user !== null;

  /**
   * Log user in and store their info
   */
  const login = useCallback((userData) => {
    localStorage.setItem('userInfo', JSON.stringify(userData));
    setUser(userData);

    if (pathname === '/login' || pathname === '/') {
      navigate('/dashboard');
    } else {
      navigate(0); // Refresh current page
    }
  }, [pathname, navigate]);

  /**
   * Log user out and clear their session
   */
  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to clear cookie
      await axios.post(`${BASE_URL}/api/v1/user/logout`, {}, {
        withCredentials: true,
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state and redirect
      setUser(null);
      localStorage.removeItem('userInfo');
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  /**
   * Check if user has one of the allowed roles
   */
  const hasRole = useCallback((allowedRoles) => {
    if (!user || !allowedRoles) return false;
    return allowedRoles.includes(user.role);
  }, [user]);

  // Enable session timeout - auto logout after 30 minutes of inactivity
  useSessionTimeout(logout, isLoggedIn);

  const value = {
    user,
    isLoggedIn,
    login,
    logout,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access auth context
 * @returns {object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
